/**
 * MovePageDialog — pick a new parent for a page. Renders a simple
 * flat list of all non-archived pages in the workspace (the page
 * being moved is filtered out, along with its descendants — the
 * server enforces the no-cycle invariant, but we keep the UI honest).
 *
 * "Top level" is a separate row at the top (parent = null). The
 * server's `requireNoCycle` walks ancestors, so even if a UI bug
 * lets a cycle through, the PATCH fails with 400 and we surface the
 * error via the standard mutation error path.
 */

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { usePageTree, useMovePage, usePage } from '@/features/notebook/pages'
import type { Page } from '@friday/shared/realtime'
import { cn } from '@/lib/utils'

export interface MovePageDialogProps {
  wsId: string
  pageId: string
  onClose: () => void
}

interface FlatRow {
  page: Page
  depth: number
  disabled?: boolean
  hint?: string
}

export function MovePageDialog({ wsId, pageId, onClose }: MovePageDialogProps) {
  const tree = usePageTree(wsId, { includeArchived: true })
  const pageQ = usePage(wsId, pageId)
  const move = useMovePage(wsId)
  const [picked, setPicked] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Compute the set of descendants of the page being moved so the
  // picker can disable them (server enforces the no-cycle invariant,
  // but a friendlier UI is better).
  const descendantIds = new Set<string>()
  if (tree.data) {
    const byParent = new Map<string | null, Page[]>()
    for (const p of tree.data) {
      const arr = byParent.get(p.parentPageId) ?? []
      arr.push(p)
      byParent.set(p.parentPageId, arr)
    }
    const walk = (id: string) => {
      for (const c of byParent.get(id) ?? []) {
        descendantIds.add(c.id)
        walk(c.id)
      }
    }
    walk(pageId)
  }

  const rows: FlatRow[] = []
  if (tree.data) {
    const sorted = [...tree.data].filter((p) => !p.archivedAt)
      .sort((a, b) => a.position - b.position)
    const byParent = new Map<string | null, Page[]>()
    for (const p of sorted) {
      const arr = byParent.get(p.parentPageId) ?? []
      arr.push(p)
      byParent.set(p.parentPageId, arr)
    }
    const visit = (parentId: string | null, depth: number) => {
      for (const p of byParent.get(parentId) ?? []) {
        const isSelf = p.id === pageId
        const isDesc = descendantIds.has(p.id)
        rows.push({
          page: p,
          depth,
          disabled: isSelf || isDesc,
          hint: isSelf ? '(this page)' : isDesc ? '(sub-page)' : undefined,
        })
        visit(p.id, depth + 1)
      }
    }
    visit(null, 0)
  }

  const submit = async () => {
    if (!pageQ.data) return
    setError(null)
    try {
      await move.mutateAsync({
        pageId,
        version: pageQ.data.version,
        parentPageId: picked,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Move failed')
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-lg border border-surface-200 bg-white shadow-2xl dark:border-surface-700 dark:bg-surface-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-surface-200 px-4 py-3 dark:border-surface-700">
          <h3 className="text-sm font-semibold">Move page</h3>
          <p className="mt-0.5 text-xs text-surface-500">
            Pick a new parent. Sub-pages of this page can't be chosen (would create a cycle).
          </p>
        </div>
        <div className="max-h-80 overflow-y-auto px-2 py-2">
          <button
            type="button"
            onClick={() => setPicked(null)}
            className={cn(
              'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-surface-100 dark:hover:bg-surface-700',
              picked === null && 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
            )}
          >
            <span className="text-sm">📁</span>
            <span>Top level</span>
          </button>
          {rows.map((r) => (
            <button
              key={r.page.id}
              type="button"
              onClick={() => !r.disabled && setPicked(r.page.id)}
              disabled={r.disabled}
              className={cn(
                'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm',
                !r.disabled && 'hover:bg-surface-100 dark:hover:bg-surface-700',
                r.disabled && 'cursor-not-allowed opacity-50',
                picked === r.page.id && 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
              )}
              style={{ paddingLeft: 8 + r.depth * 16 }}
            >
              <span className="text-sm">{r.page.icon ?? '📄'}</span>
              <span className="min-w-0 flex-1 truncate">{r.page.title}</span>
              {r.hint && <span className="text-[10px] text-surface-500">{r.hint}</span>}
            </button>
          ))}
        </div>
        {error && (
          <div className="border-t border-surface-200 px-4 py-2 text-xs text-red-600 dark:border-surface-700 dark:text-red-300">
            {error}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 border-t border-surface-200 px-4 py-3 dark:border-surface-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1 text-xs text-surface-600 hover:bg-surface-100 dark:text-surface-300 dark:hover:bg-surface-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={move.isPending}
            className="rounded bg-brand-500 px-3 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {move.isPending ? 'Moving…' : 'Move here'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
